import { useState } from 'react';

import {
  ApiRequestError,
  useLogin,
  useLogout,
  useRegister,
  useResetPassword,
} from '@jane/shared/data-access';
import { useThirdPartyAuth } from '@jane/shared/hooks';
import { sentenceCase } from '@jane/shared/util';

import { useKioskDeviceAuthorization } from '../../data-access';
import type { SsoChannel } from '../../util';

type ScreenName =
  | 'sign-in'
  | 'create-account'
  | 'forgot-password'
  | 'new-success'
  | 'success'
  | 'confirmation';

export const useKioskAuth = ({
  onLoginSuccess,
  onRegisterSuccess,
  storeId,
  userCode,
}: {
  onLoginSuccess: (ssoChannel: SsoChannel) => void;
  onRegisterSuccess: (ssoChannel: SsoChannel) => void;
  storeId: number;
  userCode: string;
}) => {
  const [loginError, setLoginError] = useState('');
  const [registerErrors, setRegisterErrors] = useState<string[]>([]);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [forgotPasswordScreen, setForgotPasswordScreen] = useState<
    'form' | 'success'
  >('form');
  const [screen, setScreen] = useState<ScreenName>();

  /*Every time the screen changes, errors should be cleared so they don't
   persist if the user returns to the screen*/
  const handleSetScreen = (screen: ScreenName) => {
    setScreen(screen);
    setLoginError('');
    setRegisterErrors([]);
    setResetPasswordError('');
  };

  /*Formats register errors from a dictionary to an array of strings*/
  const handleRegisterErrors = async (
    validations: Record<string, string[] | undefined>
  ) => {
    const errorArray: any = [];
    for (const [key, value] of Object.entries(validations)) {
      errorArray.push(sentenceCase(`${key} ${value && value.join(', ')}`));
    }

    setRegisterErrors(errorArray);
  };

  const onAuthError = (error: string) => {
    if (screen === 'sign-in' || screen === 'confirmation') {
      setLoginError(error);
    } else if (screen === 'create-account') {
      setRegisterErrors([error]);
    }
  };

  const { mutate: kioskDeviceAuthorization } = useKioskDeviceAuthorization({
    onSuccess: () => {
      setScreen(
        screen === 'sign-in' || screen === 'confirmation'
          ? 'success'
          : 'new-success'
      );
    },
    onError: (err: unknown) => {
      if (!(err instanceof ApiRequestError)) {
        throw err;
      }

      const isClientError =
        err.response?.status >= 400 && err.response?.status < 500;

      if (!isClientError) {
        throw err;
      }

      onAuthError(
        'Your link has expired, please scan the QR code on your kiosk again to sign in.'
      );
    },
  });

  const onAuthSuccess = ({
    newUser,
    ssoChannel,
  }: {
    newUser: boolean;
    ssoChannel: SsoChannel;
  }) => {
    if (newUser) {
      onRegisterSuccess(ssoChannel);
    } else {
      onLoginSuccess(ssoChannel);
    }

    kioskDeviceAuthorization({ userCode: userCode });
  };

  const { onAppleLogin, onGoogleLogin } = useThirdPartyAuth({
    onAuthSuccess,
    onAuthError: onAuthError,
    storeId,
  });

  const { mutate: loginUser } = useLogin({
    onSuccess: () => onAuthSuccess({ newUser: false, ssoChannel: null }),
    onError: (response) => {
      setLoginError(response.message);
    },
  });
  const { mutate: registerUser } = useRegister({
    onSuccess: () => onAuthSuccess({ newUser: true, ssoChannel: null }),
    onError: (validations) => {
      handleRegisterErrors(validations);
    },
  });
  const { mutate: resetPassword } = useResetPassword({
    onSuccess: () => setForgotPasswordScreen('success'),
    onError: (response) => {
      setResetPasswordError(response.message);
    },
  });
  const { mutate: logoutUser } = useLogout({
    onSuccess: () => {
      handleSetScreen('sign-in');
    },
  });

  const signInSubmit = (data: { email: string; password: string }) => {
    setLoginError('');
    loginUser({ ...data });
  };

  const createAccountSubmit = (data: {
    email: string;
    password: string;
    phone: string;
  }) => {
    setRegisterErrors([]);
    registerUser({ ...data, app_mode_at_registration: 'kiosk' });
  };

  const resetPasswordSubmit = (data: { email: string }) => {
    setResetPasswordError('');
    resetPassword(data);
  };

  return {
    loginError,
    registerErrors,
    resetPasswordError,
    forgotPasswordScreen,
    screen,
    setForgotPasswordScreen,
    handleSetScreen,
    onAppleLogin,
    onGoogleLogin,
    signInSubmit,
    createAccountSubmit,
    resetPasswordSubmit,
    logoutUser,
    onAuthSuccess,
  };
};
